import { ReactNode } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormNode } from '../../../utils/api/aboboApiTypes';
import { useFormWizard } from '../../../contexts/FormWizardContext';

export interface BaseQuestionTypeProps {
  node: FormNode;
  children: (field: any) => ReactNode;
  validate?: (value: any) => string | true;
  transform?: {
    input: (value: any) => any;
    output: (value: any) => any;
  };
}

export default function BaseQuestionType({
  node,
  children,
  validate,
  transform = { input: (v) => v, output: (v) => v }
}: BaseQuestionTypeProps) {
  const { form } = useFormWizard();
  const { t } = useTranslation();

  // Defensive check for node
  if (!node || !node.id) {
    console.error('BaseQuestionType: node or node.id is undefined', node);
    return null;
  }

  const defaultValidation = (value: any) => {
    if (node.metadata?.required && (!value || value.length === 0)) {
      return t('questionTypes.openEnded.validation.required');
    }
    return true;
  };

  const combinedValidation = (value: any) => {
    const defaultResult = defaultValidation(value);
    if (defaultResult !== true) return defaultResult;
    
    if (validate) {
      return validate(value);
    }
    
    return true;
  };

  return (
    <Controller
      name={node.id}
      control={form.control}
      defaultValue={[]}
      rules={{
        validate: combinedValidation
      }}
      render={({ field, fieldState }) => (
        <>
          {children({
            ...field,
            value: transform.input(field.value),
            onChange: (value: any) => field.onChange(transform.output(value)),
            error: fieldState.error?.message
          })}
        </>
      )}
    />
  );
}
